import React, { useState, useEffect, useCallback } from "react";
import apiClient from "../../../../api/apiClient";
import Loading from "../../../../components/Loading/Loading";
import LeadStatusBadge from "./shared/LeadStatusBadge";
import PipelineStageTag from "./shared/PipelineStageTag";
import CompanyFormModal from "./modals/CompanyFormModal";
import AddActivityModal from "./modals/AddActivityModal";
import ActivityTimeline from "./shared/ActivityTimeline";
import ActivityIcon from "./shared/ActivityIcon";
import "./CompanyDetail.css";

const BUSINESS_TYPE_LABELS = { retail: "Retail", distributor: "Distributor", cultivator: "Cultivator" };
const INDUSTRY_LABELS      = { hemp: "Hemp", rec_adult_use: "Rec / Adult Use", medical: "Medical" };
const LICENSE_TYPE_LABELS  = { EIN: "EIN", USDA_hemp: "USDA Hemp License", state: "State License" };

const InfoChip = ({ label, value }) => value ? (
    <div className="cd-chip">
        <span className="cd-chip-label">{label}</span>
        <span className="cd-chip-value">{value}</span>
    </div>
) : null; 

const EMPTY_LOC = { address: "", city: "", state: "", zipCode: "", licenseNumber: "" };

const CompanyDetail = ({ companyId, onBack, onSelectLead, currentUserId, userProfile }) => {
    const [data, setData]             = useState(null);
    const [loading, setLoading]       = useState(true);
    const [error, setError]           = useState(null);
    const [showEdit, setShowEdit]     = useState(false);
    const [confirmDelete, setConfirmDelete] = useState(false);
    const [deleting, setDeleting]     = useState(false);

    // Location form state
    const [showLocForm, setShowLocForm] = useState(false);
    const [locForm, setLocForm]         = useState({ ...EMPTY_LOC });
    const [savingLoc, setSavingLoc]     = useState(false);
    const [removingLocId, setRemovingLocId] = useState(null);

    // Activity state
    const [activityTab, setActivityTab]       = useState("company");
    const [companyActivities, setCompanyActivities] = useState([]);
    const [contactActivities, setContactActivities] = useState([]);
    const [activitiesLoading, setActivitiesLoading] = useState(false);
    const [showActivityModal, setShowActivityModal] = useState(false);
    const [editActivity, setEditActivity]           = useState(null);
    const isSuperadmin = userProfile === "superadmin";

    const fetchActivities = useCallback(async () => {
        setActivitiesLoading(true);
        try {
            const res = await apiClient.get(`/admin/crm/companies/${companyId}/activities`);
            setCompanyActivities(res.data.companyActivities || []);
            setContactActivities(res.data.contactActivities || []);
        } catch {
            // silently fail — company data still loads
        } finally {
            setActivitiesLoading(false);
        }
    }, [companyId]);

    const fetchCompany = async () => {
        setLoading(true);
        setError(null);
        try {
            const res = await apiClient.get(`/admin/crm/companies/${companyId}`);
            setData(res.data);
        } catch (err) {
            setError(err.response?.data?.error || "Failed to load company");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { fetchCompany(); fetchActivities(); }, [companyId]);

    const handleDelete = async () => {
        setDeleting(true);
        try {
            await apiClient.delete(`/admin/crm/companies/${companyId}`);
            onBack();
        } catch (err) {
            alert(err.response?.data?.error || "Failed to delete company");
        } finally {
            setDeleting(false);
        }
    };

    const handleAddLocation = async (e) => {
        e.preventDefault();
        setSavingLoc(true);
        try {
            await apiClient.post(`/admin/crm/companies/${companyId}/locations`, {
                address: locForm.address || null,
                city: locForm.city || null,
                state: locForm.state || null,
                zipCode: locForm.zipCode || null,
                licenseNumber: locForm.licenseNumber || null,
            });
            setLocForm({ ...EMPTY_LOC });
            setShowLocForm(false);
            fetchCompany();
        } catch (err) {
            alert(err.response?.data?.error || "Failed to add location");
        } finally {
            setSavingLoc(false);
        }
    };

    const handleRemoveLocation = async (locId) => {
        if (!window.confirm("Remove this location?")) return;
        setRemovingLocId(locId);
        try {
            await apiClient.delete(`/admin/crm/companies/${companyId}/locations/${locId}`);
            fetchCompany();
        } catch (err) {
            alert(err.response?.data?.error || "Failed to remove location");
        } finally {
            setRemovingLocId(null);
        }
    };

    const handleDeleteCompanyActivity = async (activityId) => {
        if (!window.confirm("Delete this activity?")) return;
        try {
            await apiClient.delete(`/admin/crm/company-activities/${activityId}`);
            setCompanyActivities((prev) => prev.filter((a) => a.id !== activityId));
        } catch (err) {
            alert(err.response?.data?.error || "Failed to delete activity");
        }
    };

    if (loading) return <div className="admin-loading-wrapper"><Loading /></div>;
    if (error)   return <div className="cd-error">{error}</div>;
    if (!data)   return null;

    const { company, locations, contacts } = data;

    return (
        <div className="company-detail">
            {/* Header */}
            <div className="cd-header">
                <button className="cd-back-btn" onClick={onBack}>← Companies</button>
                <div className="cd-header-right">
                    <button className="admin-btn" onClick={() => setShowEdit(true)}>Edit</button>
                    <button className="admin-btn admin-btn--danger" onClick={() => setConfirmDelete(true)}>Delete</button>
                </div>
            </div>

            <h1 className="cd-business-name">{company.businessName}</h1>

            {/* Info chips */}
            <div className="cd-chips">
                <InfoChip label="Type"     value={BUSINESS_TYPE_LABELS[company.businessType]} />
                <InfoChip label="Industry" value={INDUSTRY_LABELS[company.industryType]} />
                {company.licenseType && (
                    <InfoChip label={LICENSE_TYPE_LABELS[company.licenseType]} value={company.licenseNumber || "On file"} />
                )}
                {company.phone    && <InfoChip label="Phone"   value={company.phone} />}
                {company.website  && <InfoChip label="Website" value={company.website} />}
            </div>

            {/* Address */}
            {(company.businessAddress || company.city || company.state) && (
                <div className="cd-address">
                    {[company.businessAddress, company.city, company.state, company.zipCode]
                        .filter(Boolean).join(", ")}
                </div>
            )}

            {/* Notes */}
            {company.notes && (
                <div className="cd-notes-block">
                    <span className="cd-section-label">Notes</span>
                    <p className="cd-notes-text">{company.notes}</p>
                </div>
            )}

            {/* Locations */}
            <div className="cd-section">
                <div className="cd-section-header">
                    <span className="cd-section-title">
                        Locations
                        <span className="cd-section-count">{locations.length}</span>
                    </span>
                    <button
                        className="admin-btn admin-btn--sm"
                        onClick={() => setShowLocForm((v) => !v)}
                    >
                        {showLocForm ? "Cancel" : "+ Add Location"}
                    </button>
                </div>

                {showLocForm && (
                    <form className="cd-loc-form" onSubmit={handleAddLocation}>
                        <input className="admin-input" placeholder="Street address" value={locForm.address}
                            onChange={(e) => setLocForm((f) => ({ ...f, address: e.target.value }))} maxLength={300} />
                        <input className="admin-input" placeholder="City" value={locForm.city}
                            onChange={(e) => setLocForm((f) => ({ ...f, city: e.target.value }))} maxLength={100} />
                        <input className="admin-input" placeholder="State" value={locForm.state}
                            onChange={(e) => setLocForm((f) => ({ ...f, state: e.target.value }))} maxLength={100} />
                        <input className="admin-input" placeholder="Zip" value={locForm.zipCode}
                            onChange={(e) => setLocForm((f) => ({ ...f, zipCode: e.target.value }))} maxLength={20} />
                        <input className="admin-input" placeholder="License number" value={locForm.licenseNumber}
                            onChange={(e) => setLocForm((f) => ({ ...f, licenseNumber: e.target.value }))} maxLength={200} />
                        <button type="submit" className="admin-btn admin-btn--primary" disabled={savingLoc}>
                            {savingLoc ? "Saving…" : "Add"}
                        </button>
                    </form>
                )}

                {locations.length === 0 ? (
                    <p className="cd-empty">No locations added yet.</p>
                ) : (
                    <div className="cd-loc-list">
                        {locations.map((loc) => (
                            <div key={loc.id} className="cd-loc-card">
                                <div className="cd-loc-main">
                                    {[loc.address, loc.city, loc.state, loc.zipCode].filter(Boolean).join(", ") || "Address not set"}
                                </div>
                                {loc.licenseNumber && (
                                    <div className="cd-loc-license">License: {loc.licenseNumber}</div>
                                )}
                                <button
                                    className="cd-loc-remove"
                                    onClick={() => handleRemoveLocation(loc.id)}
                                    disabled={removingLocId === loc.id}
                                    title="Remove location"
                                >✕</button>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {/* Contacts */}
            <div className="cd-section">
                <div className="cd-section-header">
                    <span className="cd-section-title">
                        Contacts
                        <span className="cd-section-count">{contacts.length}</span>
                    </span>
                </div>

                {contacts.length === 0 ? (
                    <p className="cd-empty">No contacts linked to this company yet.</p>
                ) : (
                    <table className="cd-contacts-table">
                        <thead>
                            <tr>
                                <th>Name</th>
                                <th>Job Title</th>
                                <th>Email</th>
                                <th>Phone</th>
                                <th>Status</th>
                                <th>Stage</th>
                            </tr>
                        </thead>
                        <tbody>
                            {contacts.map((c) => (
                                <tr key={c.id} className="cd-contact-row" onClick={() => onSelectLead(c.id)}>
                                    <td className="cd-contact-name">{c.firstName} {c.lastName}</td>
                                    <td className="cd-contact-muted">{c.jobTitle || "—"}</td>
                                    <td className="cd-contact-muted">{c.email}</td>
                                    <td className="cd-contact-muted">{c.phone || "—"}</td>
                                    <td><LeadStatusBadge status={c.status} /></td>
                                    <td>
                                        {c.stageName
                                            ? <PipelineStageTag name={c.stageName} color={c.stageColor} />
                                            : <span className="cd-contact-muted">—</span>
                                        }
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>

            {/* Activities */}
            <div className="cd-section">
                <div className="cd-section-header">
                    <span className="cd-section-title">Activity</span>
                </div>

                <div className="crm-sub-tab-bar" style={{ marginBottom: "var(--space-4)" }}>
                    <button
                        className={`crm-sub-tab-btn ${activityTab === "company" ? "active" : ""}`}
                        onClick={() => setActivityTab("company")}
                    >
                        Company ({companyActivities.length})
                    </button>
                    <button
                        className={`crm-sub-tab-btn ${activityTab === "contacts" ? "active" : ""}`}
                        onClick={() => setActivityTab("contacts")}
                    >
                        Contacts ({contactActivities.length})
                    </button>
                </div>

                {activityTab === "company" && (
                    <div>
                        <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: "var(--space-3)" }}>
                            <button
                                className="admin-btn admin-btn--primary admin-btn--sm"
                                onClick={() => setShowActivityModal(true)}
                            >
                                + Log Activity
                            </button>
                        </div>
                        {activitiesLoading ? (
                            <p className="cd-empty">Loading…</p>
                        ) : (
                            <ActivityTimeline
                                activities={companyActivities}
                                onEdit={(a) => setEditActivity(a)}
                                onDelete={handleDeleteCompanyActivity}
                                currentUserId={currentUserId}
                                isSuperadmin={isSuperadmin}
                            />
                        )}
                    </div>
                )}

                {activityTab === "contacts" && (
                    <div>
                        <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: "var(--space-3)" }}>
                            <button
                                className="admin-btn admin-btn--sm"
                                onClick={fetchActivities}
                                disabled={activitiesLoading}
                            >
                                {activitiesLoading ? "Syncing…" : "↻ Sync"}
                            </button>
                        </div>
                        {activitiesLoading ? (
                            <p className="cd-empty">Loading…</p>
                        ) : contactActivities.length === 0 ? (
                            <p className="cd-empty">No contact activities yet. Add contacts and log activities on their pages.</p>
                        ) : (
                            <div className="crm-activity-timeline">
                                {contactActivities.map((a) => (
                                    <div key={a.id} className="crm-activity-item">
                                        <ActivityIcon type={a.type} />
                                        <div className="crm-activity-content">
                                            <div className="cd-activity-contact-badge">
                                                {a.contactFirstName} {a.contactLastName}
                                            </div>
                                            <div className="crm-activity-subject">{a.subject}</div>
                                            {a.body && <div className="crm-activity-body">{a.body}</div>}
                                            {a.outcome && (
                                                <div className="crm-activity-body" style={{ marginTop: 4, fontStyle: "italic" }}>
                                                    Outcome: {a.outcome}
                                                </div>
                                            )}
                                            <div className="crm-activity-meta">
                                                <span>{a.type}</span>
                                                <span>·</span>
                                                <span>{a.performedByFirstName} {a.performedByLastName}</span>
                                                <span>·</span>
                                                <span>{a.createdAt ? new Date(a.createdAt).toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }) : ""}</span>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                )}
            </div>

            {/* Edit modal */}
            {showEdit && (
                <CompanyFormModal
                    company={company}
                    onClose={() => setShowEdit(false)}
                    onSaved={() => { setShowEdit(false); fetchCompany(); }}
                />
            )}

            {/* Company activity modal */}
            {(showActivityModal || editActivity) && (
                <AddActivityModal
                    companyId={companyId}
                    activity={editActivity}
                    onClose={() => { setShowActivityModal(false); setEditActivity(null); }}
                    onSaved={(newActivity) => {
                        setShowActivityModal(false);
                        setEditActivity(null);
                        if (newActivity) {
                            setCompanyActivities((prev) => [newActivity, ...prev]);
                        } else {
                            fetchActivities();
                        }
                    }}
                />
            )}

            {/* Delete confirm */}
            {confirmDelete && (
                <div className="crm-modal-overlay" onClick={() => setConfirmDelete(false)}>
                    <div className="crm-modal" style={{ maxWidth: 400 }} onClick={(e) => e.stopPropagation()}>
                        <div className="crm-modal__header">
                            <span className="crm-modal__title">Delete Company?</span>
                            <button className="crm-modal__close" onClick={() => setConfirmDelete(false)}>✕</button>
                        </div>
                        <div className="crm-modal__body">
                            <p style={{ margin: "0 0 16px", fontSize: "var(--text-sm)", color: "var(--color-text-secondary)" }}>
                                This will permanently delete <strong>{company.businessName}</strong> and all its locations. Linked contacts will not be deleted but will lose the company association.
                            </p>
                        </div>
                        <div className="crm-modal__footer">
                            <button className="admin-btn" onClick={() => setConfirmDelete(false)} disabled={deleting}>Cancel</button>
                            <button className="admin-btn admin-btn--danger" onClick={handleDelete} disabled={deleting}>
                                {deleting ? "Deleting…" : "Delete Company"}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default CompanyDetail;
